// TodoBadgeList.tsx
import { useState } from 'react';
import { ActionIcon, Box, Button, Group, Text } from '@mantine/core';
import { Todo } from '@/common/types/databaseTypes'; // Todo 타입 임포트 필요
import { useTranslation } from 'react-i18next';
import { IconMinus, IconPlus, IconX } from '@tabler/icons-react';
import useFeedbackModalStore from '../hooks/useFeedbackModalStore';

interface TodoBadgeListProps {
    feedbackTodos: {
        id: string;
        todo?: Pick<Todo, 'id' | 'title' | 'enable'>;
    }[];
    removable?: boolean;
    maxVisible?: number;
    showAll?: boolean;
    marginBottom?: string;
}

/**
 * 투두 제목 뱃지 리스트
 * @param feedbackTodos - 투두 리스트
 * @param removable - 선택된 투두 리스트에서 제거 가능 여부 (피드백 모달 스토어의 setSelectedTodoMapList 함수를 통해 제거 가능)
 * @param maxVisible - 최대 표시 개수 (개수 넘어가면 see more 버튼 표시)
 * @param showAll - 모든 투두 표시 여부 (true이면 maxVisible 무시)
 * @param marginBottom - 하단 여백
 * @returns 투두 제목 뱃지 리스트
 */
const TodoBadgeList = ({
    feedbackTodos: todos,
    removable = false,
    maxVisible = 2,
    showAll = false,
    marginBottom = 'md',
}: TodoBadgeListProps) => {
    const { t } = useTranslation(['feedback']);
    const [expanded, setExpanded] = useState(false);
    const { setSelectedTodoMapList } = useFeedbackModalStore();

    if (!todos || todos.length === 0) return null;

    const visibleTodos = showAll
        ? todos
        : expanded
          ? todos
          : todos.slice(0, maxVisible);
    const hasMore = !expanded && todos.length > maxVisible;

    return (
        <Group gap="xs" mb={marginBottom} wrap="wrap">
            {visibleTodos.map(todo => (
                <Box
                    key={todo.todo?.id}
                    px="sm"
                    py={4}
                    w="fit-content"
                    bg="white"
                    style={{ border: '1px solid #DEE2E6', borderRadius: '8px' }}
                >
                    <Group gap={4}>
                        <Text size="xs">{todo.todo?.title}</Text>
                        {removable && (
                            <ActionIcon
                                variant="subtle"
                                color="gray"
                                size="xs"
                                onClick={() => {
                                    const updatedTodos = todos
                                        .filter(
                                            t => t.todo?.id !== todo.todo?.id
                                        )
                                        .map(t => ({
                                            id: t.todo?.id,
                                            title: t.todo?.title,
                                        }));
                                    setSelectedTodoMapList(updatedTodos);
                                }}
                            >
                                <IconX size={12} color="black" />
                            </ActionIcon>
                        )}
                    </Group>
                </Box>
            ))}

            {hasMore && !showAll && (
                <Button
                    variant="default"
                    color="gray"
                    size="compact-sm"
                    radius={8}
                    onClick={() => setExpanded(true)}
                    leftSection={<IconPlus size={12} />}
                    styles={{
                        section: {
                            marginRight: '4px',
                        },
                    }}
                >
                    <Text size="xs">
                        {t('todoBadgeList.seeMore', {
                            count: todos.length - maxVisible,
                        })}
                    </Text>
                </Button>
            )}

            {expanded && !showAll && (
                <Button
                    variant="default"
                    color="gray"
                    size="compact-sm"
                    radius={8}
                    onClick={() => setExpanded(false)}
                    leftSection={<IconMinus size={12} />}
                    styles={{
                        section: {
                            marginRight: '4px',
                        },
                    }}
                >
                    <Text size="xs">{t('todoBadgeList.collapse')}</Text>
                </Button>
            )}
        </Group>
    );
};

export default TodoBadgeList;
